/**
 * Benefits per Tier: domain types, contract and error class.
 *
 * The runtime implementation lives in `httpBenefitsPerTierService.ts` and is
 * wired into `BenefitsPerTierProvider`. Tests can supply their own
 * implementation of `BenefitsPerTierService` via the Provider's `service`
 * prop without depending on this file beyond the type exports.
 *
 * Mirrors the per-entity Pending model used by Market Categories: each
 * benefit can have its own staged Create / Update / Rename / Delete in
 * Cosmos, identified by the benefit name (or by the pre-rename name for
 * pending Renames).
 */

import type { TierKey } from '../constants/tiers';

export interface PendingBenefitInfo {
  operation: 'create' | 'update' | 'rename' | 'delete';
  createdAt: string;
  createdBy: string;
  /**
   * For `rename` pendings: the name the benefit had before the rename.
   * The current `Benefit.name` reflects the post-rename name.
   */
  previousName?: string;
  /**
   * Per-tier eligibility from the committed SQL state. Provided for
   * `update`, `rename`, and `delete` so the UI can highlight which
   * checkboxes changed and what value they held before. Absent for
   * `create` (no prior state).
   */
  previousEligibility?: Record<TierKey, boolean>;
}

/**
 * Metadata describing the most-recently applied change for a benefit, as
 * surfaced by `GET /api/ProgramConfig/lastApplied/benefitsPerTier`. Used by
 * the UI to render "Last updated: {when} by {who}" so reviewers can tell this
 * month's approved data from stale data.
 */
export interface LastUpdatedInfo {
  /** ISO-8601 timestamp of when the change was committed to SRA SQL. */
  at: string;
  /** UPN of the user who staged the change. */
  by: string;
}

export interface Benefit {
  id: string;
  name: string;
  eligibility: Record<TierKey, boolean>;
  /**
   * Present when this row reflects (or differs from) a pending change
   * staged in Cosmos that has not yet been applied to SQL.
   */
  pending?: PendingBenefitInfo;
  /**
   * Most-recent Applied change for this benefit. Drives the "Last updated"
   * column. Absent when the row has never had an applied change tracked
   * through Cosmos (legacy SQL-only data, or post-rename).
   */
  lastUpdated?: LastUpdatedInfo;
}

export const EMPTY_ELIGIBILITY: Record<TierKey, boolean> = Object.freeze({
  silver: false,
  gold: false,
  platinum: false,
}) as Record<TierKey, boolean>;

export class BenefitsPerTierError extends Error {
  code:
    | 'DUPLICATE_NAME'
    | 'EMPTY_NAME'
    | 'EMPTY_TIERS'
    | 'NOT_FOUND'
    | 'SERVER_ERROR';
  constructor(code: BenefitsPerTierError['code'], message: string) {
    super(message);
    this.code = code;
    this.name = 'BenefitsPerTierError';
  }
}

export interface BenefitsPerTierService {
  /**
   * Fetches the merged view: committed SQL state with all pending
   * per-benefit changes overlaid on top. Rows that differ from SQL (or
   * that were introduced or removed by a pending) carry a `pending`
   * marker.
   */
  list(): Promise<Benefit[]>;

  /**
   * Stages a Create or Update for the given benefit's eligibility matrix.
   * The backend derives Create vs Update from current state.
   */
  upsertBenefit(name: string, eligibility: Record<TierKey, boolean>): Promise<void>;

  /**
   * Stages a Rename of an existing benefit.
   */
  renameBenefit(currentName: string, newName: string): Promise<void>;

  /**
   * Stages a Delete of the given benefit.
   */
  deleteBenefit(name: string): Promise<void>;

  /**
   * Discards any uncommitted change for the given benefit. Idempotent.
   * Accepts either the post-rename or pre-rename name.
   */
  cancelPendingForBenefit(name: string): Promise<void>;
}
